import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth, API } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import axios from "axios";
import { Send, X, Maximize2, Minimize2, MessageSquare, Coffee, Briefcase } from "lucide-react";

// Available chat rooms (site-wide)
const CHAT_ROOMS = [
  { id: "social_global", name: "Community Social", icon: Coffee, color: "#8DB5A0", desc: "Fun & casual chat" },
  { id: "discussions_global", name: "Professional Lounge", icon: Briefcase, color: "#6B4E8A", desc: "Clinical & career talk" },
];

const LAST_SEEN_KEY = "dhdh_chat_last_seen";

const getLastSeenMap = () => {
  try {
    return JSON.parse(localStorage.getItem(LAST_SEEN_KEY) || "{}");
  } catch {
    return {};
  }
};
const setLastSeenFor = (roomId, iso) => {
  const map = getLastSeenMap();
  map[roomId] = iso;
  localStorage.setItem(LAST_SEEN_KEY, JSON.stringify(map));
};

const TypingDots = () => (
  <span className="inline-flex items-center gap-0.5" aria-hidden="true">
    <span className="w-1 h-1 rounded-full bg-[#64748B] animate-bounce" style={{ animationDelay: "0ms" }} />
    <span className="w-1 h-1 rounded-full bg-[#64748B] animate-bounce" style={{ animationDelay: "150ms" }} />
    <span className="w-1 h-1 rounded-full bg-[#64748B] animate-bounce" style={{ animationDelay: "300ms" }} />
  </span>
);

const ChatPanel = ({ room, onBack, onClose, onMinimize, onSawMessage }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [typingUsers, setTypingUsers] = useState([]);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const lastTimestamp = useRef("");
  const pollRef = useRef(null);
  const heartbeatRef = useRef(null);
  const typingPollRef = useRef(null);
  const lastTypingSignal = useRef(0);

  // Mark as seen helper
  const markSeen = useCallback((iso) => {
    if (iso) {
      setLastSeenFor(room.id, iso);
      onSawMessage?.(room.id, iso);
    }
  }, [room.id, onSawMessage]);

  // Load initial messages
  useEffect(() => {
    const loadMessages = async () => {
      try {
        const res = await axios.get(`${API}/live-chat/rooms/${room.id}/messages?limit=50`);
        setMessages(res.data);
        if (res.data.length > 0) {
          const latest = res.data[res.data.length - 1].created_at;
          lastTimestamp.current = latest;
          markSeen(latest);
        } else {
          // Empty room - mark seen with now
          markSeen(new Date().toISOString());
        }
      } catch { /* silent */ }
    };
    loadMessages();
  }, [room.id, markSeen]);

  // Poll for new messages every 2s
  useEffect(() => {
    pollRef.current = setInterval(async () => {
      if (!lastTimestamp.current) return;
      try {
        const res = await axios.get(`${API}/live-chat/rooms/${room.id}/new?after=${encodeURIComponent(lastTimestamp.current)}`);
        if (res.data.length > 0) {
          setMessages(prev => {
            const ids = new Set(prev.map(m => m.message_id));
            const newMsgs = res.data.filter(m => !ids.has(m.message_id));
            return [...prev, ...newMsgs];
          });
          const latest = res.data[res.data.length - 1].created_at;
          lastTimestamp.current = latest;
          markSeen(latest);
        }
      } catch { /* silent */ }
    }, 2000);
    return () => clearInterval(pollRef.current);
  }, [room.id, markSeen]);

  // Heartbeat for online presence every 15s
  useEffect(() => {
    const beat = async () => {
      try {
        const res = await axios.get(`${API}/live-chat/rooms/${room.id}/online`);
        setOnlineUsers(res.data);
      } catch { /* silent */ }
    };
    beat();
    heartbeatRef.current = setInterval(beat, 15000);
    return () => clearInterval(heartbeatRef.current);
  }, [room.id]);

  // Poll typing indicators every 2s
  useEffect(() => {
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/live-chat/rooms/${room.id}/typing`);
        setTypingUsers(res.data);
      } catch { /* silent */ }
    };
    poll();
    typingPollRef.current = setInterval(poll, 2000);
    return () => clearInterval(typingPollRef.current);
  }, [room.id]);

  // Clear self-typing on unmount
  useEffect(() => {
    return () => {
      axios.post(`${API}/live-chat/rooms/${room.id}/typing`, { is_typing: false }).catch(() => {});
    };
  }, [room.id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, typingUsers]);

  const handleInputChange = (e) => {
    setInput(e.target.value);
    // Throttle typing signal to once every 2s
    const now = Date.now();
    if (e.target.value && now - lastTypingSignal.current > 2000) {
      lastTypingSignal.current = now;
      axios.post(`${API}/live-chat/rooms/${room.id}/typing`, { is_typing: true }).catch(() => {});
    }
  };

  const handleSend = async () => {
    if (!input.trim() || sending) return;
    setSending(true);
    const text = input.trim();
    setInput("");
    try {
      const res = await axios.post(`${API}/live-chat/rooms/${room.id}/messages`, { content: text });
      setMessages(prev => {
        const ids = new Set(prev.map(m => m.message_id));
        if (ids.has(res.data.message_id)) return prev;
        return [...prev, res.data];
      });
      lastTimestamp.current = res.data.created_at;
      markSeen(res.data.created_at);
      lastTypingSignal.current = 0;
      // Clear typing state after send
      axios.post(`${API}/live-chat/rooms/${room.id}/typing`, { is_typing: false }).catch(() => {});
    } catch {
      setInput(text);
    } finally {
      setSending(false);
    }
  };

  const timeStr = (d) => new Date(d).toLocaleTimeString("en-CA", { hour: "2-digit", minute: "2-digit" });
  const Icon = room.icon;
  const typingLabel = typingUsers.length === 0 ? null
    : typingUsers.length === 1 ? `${typingUsers[0].user_name} is typing`
    : typingUsers.length === 2 ? `${typingUsers[0].user_name} and ${typingUsers[1].user_name} are typing`
    : `${typingUsers.length} people are typing`;

  return (
    <div className="fixed bottom-20 right-6 z-[60] w-[min(calc(100vw-2rem),380px)] h-[min(calc(100vh-7rem),560px)] bg-white rounded-2xl shadow-2xl border border-[#E5DFEC] flex flex-col overflow-hidden" data-testid="live-chat-panel">
      {/* Header */}
      <div className="px-4 py-3 flex items-center justify-between shrink-0" style={{ backgroundColor: room.color }}>
        <button onClick={onBack} className="flex items-center gap-2 min-w-0 text-left hover:opacity-80 transition-opacity" data-testid="chat-back-btn">
          <Icon className="w-4 h-4 text-white shrink-0" />
          <div className="min-w-0">
            <p className="text-white text-sm font-medium truncate leading-tight">{room.name}</p>
            <p className="text-white/70 text-[10px] flex items-center gap-1 leading-tight">
              <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] animate-pulse" />
              {onlineUsers.length} online
            </p>
          </div>
        </button>
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={onMinimize} className="p-1.5 hover:bg-white/10 rounded-lg" data-testid="chat-minimize-btn"><Minimize2 className="w-3.5 h-3.5 text-white" /></button>
          <button onClick={onClose} className="p-1.5 hover:bg-white/10 rounded-lg" data-testid="chat-close-btn"><X className="w-4 h-4 text-white" /></button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-[#FAF7F2]">
        {messages.length === 0 && (
          <div className="text-center py-8">
            <Icon className="w-10 h-10 mx-auto mb-2 opacity-30" style={{ color: room.color }} />
            <p className="text-xs text-[#64748B]">No messages yet. Break the ice!</p>
          </div>
        )}
        {messages.map((msg) => {
          const isMe = msg.sender_id === user?.user_id;
          return (
            <div key={msg.message_id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[78%] ${isMe ? 'order-2' : ''}`}>
                {!isMe && <p className="text-[10px] text-[#64748B] mb-0.5 ml-1 font-medium">{msg.sender_name}</p>}
                <div className={`px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words ${isMe ? 'text-white rounded-br-md' : 'bg-white border border-[#E5DFEC] text-[#6B4E8A] rounded-bl-md'}`} style={isMe ? { backgroundColor: room.color } : {}}>
                  {msg.content}
                </div>
                <p className={`text-[10px] text-[#94A3B8] mt-0.5 ${isMe ? 'text-right mr-1' : 'ml-1'}`}>{timeStr(msg.created_at)}</p>
              </div>
            </div>
          );
        })}
        {typingLabel && (
          <div className="flex justify-start" data-testid="typing-indicator">
            <div className="bg-white border border-[#E5DFEC] rounded-2xl rounded-bl-md px-3 py-2 flex items-center gap-2">
              <TypingDots />
              <span className="text-[11px] text-[#64748B] italic">{typingLabel}</span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-3 border-t border-[#E5DFEC] bg-white shrink-0">
        <div className="flex gap-2">
          <Input
            value={input}
            onChange={handleInputChange}
            onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleSend()}
            placeholder="Type a message..."
            className="bg-[#FAF7F2] border-[#E5DFEC] text-sm h-9 flex-1"
            data-testid="live-chat-input"
          />
          <Button
            onClick={handleSend}
            disabled={!input.trim() || sending}
            size="sm"
            className="text-white rounded-full h-9 w-9 p-0 shrink-0 hover:opacity-90"
            style={{ backgroundColor: room.color }}
            data-testid="live-chat-send"
          >
            <Send className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
};

const RoomPicker = ({ onSelect, onClose, unreadCounts }) => (
  <div className="fixed bottom-20 right-6 z-[60] w-[min(calc(100vw-2rem),320px)] bg-white rounded-2xl shadow-2xl border border-[#E5DFEC] overflow-hidden" data-testid="chat-room-picker">
    <div className="bg-[#6B4E8A] px-4 py-3 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <MessageSquare className="w-4 h-4 text-white" />
        <p className="text-white text-sm font-medium">Live Chat Rooms</p>
      </div>
      <button onClick={onClose} className="p-1.5 hover:bg-white/10 rounded-lg" data-testid="chat-picker-close">
        <X className="w-4 h-4 text-white" />
      </button>
    </div>
    <div className="p-2">
      {CHAT_ROOMS.map((room) => {
        const Icon = room.icon;
        const unread = unreadCounts[room.id] || 0;
        return (
          <button
            key={room.id}
            onClick={() => onSelect(room)}
            className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-[#F0ECF7] transition-colors text-left"
            data-testid={`chat-room-${room.id}`}
          >
            <div className="relative shrink-0">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: room.color }}>
                <Icon className="w-5 h-5 text-white" />
              </div>
              {unread > 0 && (
                <span
                  className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 rounded-full bg-[#EF4444] text-white text-[10px] font-bold flex items-center justify-center border-2 border-white"
                  data-testid={`unread-badge-${room.id}`}
                >
                  {unread >= 99 ? "99+" : unread}
                </span>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-[#6B4E8A] truncate flex items-center gap-2">
                {room.name}
                {unread > 0 && <span className="text-[10px] text-[#EF4444] font-semibold">• {unread} new</span>}
              </p>
              <p className="text-xs text-[#64748B] truncate">{room.desc}</p>
            </div>
          </button>
        );
      })}
    </div>
    <div className="px-4 pb-3 pt-1">
      <p className="text-[10px] text-[#94A3B8] text-center">Real-time chat with fellow hygienists</p>
    </div>
  </div>
);

const FloatingChatWidget = () => {
  const { user } = useAuth();
  // states: 'closed' | 'picker' | 'minimized' | 'chat'
  const [mode, setMode] = useState("closed");
  const [activeRoom, setActiveRoom] = useState(null);
  const [totalOnline, setTotalOnline] = useState(0);
  const [unreadCounts, setUnreadCounts] = useState({}); // {room_id: count}

  const totalUnread = Object.values(unreadCounts).reduce((a, b) => a + b, 0);

  // Poll online + unread every 10s (always, so badge updates even when closed)
  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    const fetchData = async () => {
      // Unread counts
      try {
        const lastSeenMap = getLastSeenMap();
        const rooms = {};
        CHAT_ROOMS.forEach(r => { rooms[r.id] = lastSeenMap[r.id] || null; });
        const res = await axios.post(`${API}/live-chat/unread-counts`, { rooms });
        if (!cancelled) setUnreadCounts(res.data || {});
      } catch { /* silent */ }

      // Online counts (only when not in chat, since ChatPanel has its own heartbeat)
      if (mode !== "chat") {
        try {
          const results = await Promise.all(
            CHAT_ROOMS.map(r => axios.get(`${API}/live-chat/rooms/${r.id}/online`).catch(() => ({ data: [] })))
          );
          if (cancelled) return;
          const ids = new Set();
          results.forEach(r => (r.data || []).forEach(u => ids.add(u.user_id)));
          setTotalOnline(ids.size);
        } catch { /* silent */ }
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [user, mode]);

  // Called by ChatPanel when it sees a message — update local unread immediately
  const handleSawMessage = useCallback((roomId) => {
    setUnreadCounts(prev => ({ ...prev, [roomId]: 0 }));
  }, []);

  if (!user) return null;

  if (mode === "closed") {
    return (
      <button
        onClick={() => setMode("picker")}
        className="fixed bottom-20 right-6 z-[55] bg-[#6B4E8A] text-white rounded-full shadow-2xl hover:bg-[#553D6F] transition-all hover:scale-105 flex items-center gap-2 pl-4 pr-5 py-3 group"
        data-testid="floating-chat-launcher"
      >
        <div className="relative">
          <MessageSquare className="w-5 h-5" />
          {totalUnread > 0 ? (
            <span
              className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-[#EF4444] text-white text-[10px] font-bold flex items-center justify-center border-2 border-[#6B4E8A]"
              data-testid="total-unread-badge"
            >
              {totalUnread >= 99 ? "99+" : totalUnread}
            </span>
          ) : totalOnline > 0 ? (
            <span className="absolute -top-1.5 -right-1.5 w-2.5 h-2.5 rounded-full bg-[#10B981] border-2 border-[#6B4E8A]" />
          ) : null}
        </div>
        <span className="text-sm font-medium hidden sm:inline">Chat</span>
        {totalOnline > 0 && totalUnread === 0 && (
          <span className="text-[10px] bg-[#10B981] text-white px-1.5 py-0.5 rounded-full font-medium" data-testid="chat-online-count">
            {totalOnline} online
          </span>
        )}
      </button>
    );
  }

  if (mode === "picker") {
    return (
      <RoomPicker
        onSelect={(r) => { setActiveRoom(r); setMode("chat"); }}
        onClose={() => setMode("closed")}
        unreadCounts={unreadCounts}
      />
    );
  }

  if (mode === "minimized" && activeRoom) {
    const Icon = activeRoom.icon;
    const unread = unreadCounts[activeRoom.id] || 0;
    return (
      <button
        onClick={() => setMode("chat")}
        className="fixed bottom-20 right-6 z-[55] text-white rounded-full shadow-2xl hover:opacity-90 transition-all flex items-center gap-2 pl-4 pr-5 py-3 relative"
        style={{ backgroundColor: activeRoom.color }}
        data-testid="chat-minimized-btn"
      >
        <Icon className="w-5 h-5" />
        <span className="text-sm font-medium">{activeRoom.name}</span>
        <Maximize2 className="w-3.5 h-3.5" />
        {unread > 0 && (
          <span className="absolute -top-2 -right-2 min-w-[20px] h-5 px-1.5 rounded-full bg-[#EF4444] text-white text-[10px] font-bold flex items-center justify-center border-2 border-white">
            {unread >= 99 ? "99+" : unread}
          </span>
        )}
      </button>
    );
  }

  if (mode === "chat" && activeRoom) {
    return (
      <ChatPanel
        room={activeRoom}
        onBack={() => setMode("picker")}
        onClose={() => { setMode("closed"); setActiveRoom(null); }}
        onMinimize={() => setMode("minimized")}
        onSawMessage={handleSawMessage}
      />
    );
  }

  return null;
};

export default FloatingChatWidget;
